/*
* Report pages: component funding
*/

import * as FastSort from 'fast-sort';
import * as _ from 'lodash';
import * as React from 'react';

import { isLevel3Report } from 'src/order/utils';
import { AppDataConstants } from '../../../common/app-data-constants';
import { Formatters } from '../../../common/formatters';
import { transformComponentFundingItems } from '../../../report/componentFunding';
import * as ReportComponents from '../report-components';

const MAX_COMPONENT_NAME_LEN_WRAP = 41;
const MAX_QUANTITY_LEN_WRAP = 24;

export const Render = ({ registerFunc, refFunc, scrollToFunc, mrcValue, maxTableRowsPerPage = 0, pageTitle = '', className = '', pageConfiguration, pageCount, reportData, appText, firstPass, sectionPages, ...rest }) => {
    const sectionName = ReportComponents.ReportSectionNames.componentFundingInformation;
    const ReportPageWithRegister = ReportComponents.withPageRegistration(ReportComponents.ReportPageTableData, sectionName, registerFunc, refFunc, scrollToFunc);
    const getTableRowKey = (id, secName, tabName = 'data') => ReportComponents.getRowKey(id, secName, tabName);

    const { formData, association, inventory, currentOrderChart, percentFunded, currentOrder } = reportData;
    const chunkSize = (maxTableRowsPerPage > 0) ? maxTableRowsPerPage : _.get(pageConfiguration, 'maxTableRowsPerPage', 15);
    const reportOptionsGroupComponents = Formatters.getBool(currentOrderChart, 'groupComponents');
    const reportOptionsGroupComponentsPictureSection = Formatters.getBool(currentOrderChart, 'groupComponentsPictureSection');

    const buildRowsNew = (data: any[], tableName: string = 'tbl') => {
        const dataItems = _.filter(data, (o) => !Formatters.getBool(o, 'placeholder'));
        const compEvalItems = _.filter(sectionPages, ['pageTitle', 'Component Evaluation']);

        const compDataSetRaw = _.map(compEvalItems, (item) => {
            const compDataItem: any = _.pick(item, ['pageKey', 'pageNumber']);
            return compDataItem;
        });
        let compDataSet = _.cloneDeep(compDataSetRaw);
        compDataSet = _.forEach(compDataSet, (i) => { i.pageKey = _.trim(i.pageKey); });
        let rows: any = _(dataItems)
            .map((item: any, ind: number, arr: any[]) => {
                let res = _.stubArray();

                const isFirstPage = Formatters.getBool(item, 'group.first');
                const isLastPage = Formatters.getBool(item, 'group.last');
                const isDisplayItemOnly = Formatters.getBool(item, 'wrappedItemRow');

                if (isFirstPage && reportOptionsGroupComponents) {
                    const headerRow = (isFirstPage) ? buildGroupHeaderRow(item) : undefined;
                    const hasHeaderRow = (headerRow) ? true : false;

                    if (hasHeaderRow) { res.push(headerRow); }
                }

                let componentNameData = Formatters.getStr(item, 'componentName', '', true);
                if (!firstPass && formData.showPageNumbers) {
                    const getCompData = (pageKey: string) => {
                        // @ts-ignore
                        // return (compDataSet).find(['pageKey', pageKey]);
                        return _.find(compDataSet, i => i.pageKey === pageKey);
                    };
                    const componentName = isDisplayItemOnly ? item.pre_wrappedName : item.componentName;
                    const pageNum = _.get(getCompData('component-evaluation-' + componentName + ' ' + Formatters.getStr(item, 'group.subgroupTitle', '', true)), 'pageNumber');
                    if (!isLevel3Report(currentOrder) && pageNum !== undefined) {
                        componentNameData += ` (pp. ${pageNum})`;
                    }
                }

                res.push(
                    <tr key={getTableRowKey(`${Formatters.getStr(item, '_id')}-${Formatters.getStr(item, 'wrappedItemRow_sort')}`, sectionName, `${tableName}-cfi-row-component`)}>
                        <td className="text-center">{(!isDisplayItemOnly) ? Formatters.getStr(item, 'componentId') : <span />}</td>
                        <td className="text-center">{componentNameData}</td>
                        <td className="text-center">{(!isDisplayItemOnly) ? Formatters.getStr(item, 'usefulLife') : <span />}</td>
                        <td className="text-center">{(!isDisplayItemOnly) ? Formatters.getInt(item, 'remainingUsefulLife') : <span />}</td>
                        <td className="text-center">{Formatters.getStr(item, 'quantity')}</td>
                        <td className="text-right">{(!isDisplayItemOnly) ? <ReportComponents.Currency value={Formatters.getFloat(item, 'avgCost')} decimals={0} /> : <span />}</td>
                        <td className="text-right">{(!isDisplayItemOnly) ? <ReportComponents.Currency value={Formatters.getFloat(item, 'idealBal')} decimals={0} /> : <span />}</td>
                        { /* <td className="text-right">{(!isDisplayItemOnly) ? <ReportComponents.Currency value={Formatters.getFloat(item, 'currentFundBalance')} decimals={0} /> : <span />}</td> */}
                        <td className="text-right">{(!isDisplayItemOnly) ? <ReportComponents.Currency value={Formatters.getFloat(item, 'cfi_curFundBalance')} decimals={0} /> : <span />}</td>
                        <td className="text-right">{(!isDisplayItemOnly) ? <ReportComponents.Currency value={Formatters.getFloat(item, 'monthly')} /> : <span />}</td>
                    </tr>
                );

                if (isLastPage && reportOptionsGroupComponents) {
                    const footerRow = (isLastPage) ? buildGroupSummaryRow(item) : undefined;
                    const hasFooterRow = (footerRow) ? true : false;

                    if (hasFooterRow) { res.push(footerRow); }
                }

                return res;
            })
            .value();

        return _.flattenDeep(rows);
    };

    const buildFundBalance = (pctFunded: any = 0) => {
        return (
            <div className="pdf-page-component-funding-cfb">
                <span>{appText('page.component-funding-information.current-fund-balance')}: </span>
                <span className="pdf-page-component-funding-cfb-percent">
                    <ReportComponents.Percentage value={pctFunded / 100} decimals={0} />
                </span>
            </div>
        );
    };

    const transformedInvItems = transformComponentFundingItems(inventory, formData.begBalance, mrcValue);
    const invSort = (reportOptionsGroupComponents) ?
        _.sortBy(transformedInvItems, ['subgroup', '__componentSortBase', '__componentSortDecimal', 'componentName_sort', 'wrappedItemRow_sort', '__componentSortStable'])
        : _.sortBy(transformedInvItems, ['__componentSortBase', '__componentSortDecimal', 'componentName_sort', 'wrappedItemRow_sort', '__componentSortStable']);

    let totalIdealBalance = _.sumBy(invSort, 'idealBalance');
    let year = 0;
    let runningVal = Formatters.getFloat(formData, 'begBalance');
    _.forEach(invSort, (c) => {
        let average = 0;
        let usefulLife = 0;
        let oneTime = false;
        let notApplicable = false;
        let remainingUsefulLife = 0;
        let idealBalance = 0;
        const isDisplayItemOnly = Formatters.getBoolFalse(c, 'wrappedItemRow');

        idealBalance = Formatters.getFloat(c, 'idealBal');
        average = Formatters.getFloat(c, 'avgCost');
        oneTime = (Formatters.getStr(c, 'usefulLife') === AppDataConstants.UsefulLifeOptions.OneTimeExpense) ? true : false;
        notApplicable = (Formatters.getStr(c, 'usefulLife') === AppDataConstants.UsefulLifeOptions.NotApplicable) ? true : false;
        if (!oneTime && !notApplicable) { usefulLife = Formatters.getFloat(c, 'usefulLife'); }
        remainingUsefulLife = Formatters.getFloat(c, 'remainingUsefulLife');

        if (!isDisplayItemOnly) {
            // calculate runningval
            if (oneTime) {
                runningVal = runningVal - (average / (remainingUsefulLife + 1));
            } else if (notApplicable) {
                runningVal = runningVal;
            } else {
                if (usefulLife >= (remainingUsefulLife - year)) {
                    runningVal = runningVal - idealBalance;
                }
            }
        }

        _.set(c, 'runningVal', runningVal);
        _.set(c, 'oneTime', oneTime);
    });

    const assocDepVal = _.sumBy(invSort, 'signAsDollar');

    // set currentfundbalance
    const begBalance = Formatters.getFloat(formData, 'begBalance', 2);
    _.forEach(invSort, (c, cInd) => {
        let cfb = Formatters.getFloat(c, 'currentFundBalance', 2); // 20191004 Was = 0
        let curBal = Formatters.getFloat(c, 'curBal', 2); // 20191004 Was = 0
        let monthly = 0;
        const idealBal = Formatters.getFloat(c, 'idealBal', 2);
        const cRunningVal = Formatters.getFloat(c, 'runningVal', 2);
        const signAsDollar = Formatters.getFloat(c, 'signAsDollar');
        const mrcVal = Formatters.getFloat(formData, 'mrcValue');

        const msgBase = `report-page-component-funding:render invSort.forEach cInd ${cInd} cfb ${cfb} curBal ${curBal} idealBal ${idealBal} cRunningVal ${cRunningVal} begBalance ${begBalance}`;
        let msgLog = '';

        const ignoreWrappedItemRow = Formatters.getBool(c, 'wrappedItemRow');
        if (!ignoreWrappedItemRow) {
            if ((cfb === 0) || (cfb === curBal)) {
                if (begBalance === 0) {
                    let cfbPrev = cfb;
                    let cfbNext = 0;

                    msgLog = `${msgBase}: case0 cfbPrev ${cfbPrev} cfbNext ${cfbNext}`;
                    cfb = cfbNext;

                } else if (totalIdealBalance < begBalance) {
                    let cfbPrev = cfb;
                    let cfbNext = (idealBal * (begBalance / totalIdealBalance));

                    msgLog = `${msgBase}: case1 cfbPrev ${cfbPrev} cfbNext ${cfbNext}; cfbNext = idealBal ${idealBal} * (begBalance ${begBalance} / totalIdealBalance ${totalIdealBalance}));`;
                    cfb = cfbNext;

                } else if (_.round(cRunningVal, 2) === _.round((begBalance - idealBal), 2)) {
                    let cfbPrev = cfb;
                    let cfbNext = idealBal;

                    msgLog = `${msgBase}: case2 cfbPrev ${cfbPrev} cfbNext ${cfbNext}: cfbNext = idealBal ${idealBal}`;
                    cfb = cfbNext;

                } else if ((cRunningVal < 0) && ((idealBal + cRunningVal) > 0)) {
                    let cfbPrev = cfb;
                    let cfbNext = cRunningVal + idealBal;

                    msgLog = `${msgBase}: case3 cfbPrev ${cfbPrev} cfbNext ${cfbNext}: cfbNext = cRunningVal ${cRunningVal} + idealBal ${idealBal};`;
                    cfb = cfbNext;

                } else if ((idealBal + cRunningVal) < 0) {
                    let cfbPrev = cfb;
                    let cfbNext = 0;

                    msgLog = `${msgBase}: case4 cfbPrev ${cfbPrev} cfbNext: cfbNext = 0`;
                    cfb = cfbNext;

                } else {
                    let cfbPrev = cfb;
                    let cfbNext = idealBal;

                    msgLog = `${msgBase}: case5 cfbPrev ${cfbPrev} cfbNext ${cfbNext}: cfbNext = idealBal ${idealBal};`;
                    cfb = cfbNext;
                }
            }

            monthly = (mrcVal * (signAsDollar / assocDepVal));

        }
    });

    // GROUP ITEMS BY SUBGROUP
    const grouped = _(invSort)
        .groupBy('subgroup')
        .toPairs()
        .value();

    const subgroups = _.flattenDeep(
        grouped.map((grp, gInd) => {
            const sg = (_.find(association.inventorySubgroups, ['groupCode', Formatters.getStr(grp, '0', 'MISSING_GROUPCODE', true)])) ?
                _.find(association.inventorySubgroups, ['groupCode', Formatters.getStr(grp, '0', 'MISSING_GROUPCODE', true)]) :
                _.find(association.inventorySubgroups, ['groupCode', _.toString(_.parseInt(grp[0]))]);
            const sgMissing = (!sg || !sg.groupCode) ? true : false;

            if (sgMissing) { return null; }

            const groupItems = _.get(grp, '[1]', []);
            const sumAvgCostsPerUL = _.sumBy(groupItems, 'avgCost');
            const sumIdealBals = _.sumBy(groupItems, 'idealBal');
            const sumCurrentFundBals = _.sumBy(groupItems, 'cfi_curFundBalance'); // 20191004 _.sumBy(g[1], 'currentFundBalance');
            const sumMonthlies = _.sumBy(groupItems, 'monthly');

            const groupItemsSorted = FastSort([...groupItems]).asc([
                g => _.get(g, '__componentSortBase', 0),
                g => _.get(g, '__componentSortDecimal', -1),
                g => g.componentName_sort,
                g => g._id,
                g => g.wrappedItemRow_sort,
            ]);

            const items = groupItemsSorted.map((groupItem, groupItemInd, iArr) => {
                return _.merge(
                    {},
                    groupItem,
                    {
                        group: {
                            first: (groupItemInd === 0) ? true : false,
                            last: (groupItemInd === _.size(iArr) - 1) ? true : false,
                            groupInd: groupItemInd,
                            gInd: gInd,
                            gItemInd: groupItemInd,
                            subgroupTitle: sg.title,
                            subgroupId: sg._id,
                            subgroupLabel: sg.label,
                            subTotals: {
                                avgCosts: sumAvgCostsPerUL,
                                idealBals: sumIdealBals,
                                currentFundBals: sumCurrentFundBals,
                                monthly: sumMonthlies,
                            },
                        }
                    }
                );
            });

            return items;
        })
    );

    const buildGroupHeaderRow = (itemData: any) => {
        const groupTitle = Formatters.getStr(itemData, 'group.subgroupTitle');
        const groupSubgroupCode = Formatters.getStr(itemData, 'subgroup');

        const result = (
            <tr key={getTableRowKey(`summary-row-header-subgroup-${groupSubgroupCode}`, sectionName, 'tbl-cfi-group-header')} className="pdf-data-table-summary-row">
                <td colSpan={9} className="pdf-data-table-summary-row-colspan pdf-data-table-group-header-row text-left">{groupTitle}</td>
            </tr>
        );

        return result;
    };

    const buildGroupSummaryRow = (groupData: any) => {
        const sumAvgCost = _.get(groupData, 'group.subTotals.avgCosts', 0);
        const sumIdealBal = _.get(groupData, 'group.subTotals.idealBals', 0);
        const sumCurBal = _.get(groupData, 'group.subTotals.currentFundBals', 0);
        const sumMonthly = _.get(groupData, 'group.subTotals.monthly', 0);

        const result = (
            <tr key={getTableRowKey(`summary-row-${_.get(groupData, 'subgroup')}`, sectionName, 'tbl-cfi-group-summary')} className="pdf-data-table-summary-row">
                <td colSpan={5} className="pdf-data-table-summary-row-colspan pdf-data-table-summary-row-subtotal-title text-right">{appText('page.component-funding-information.subtotals')}:</td>
                <td className="text-right pdf-data-table-summary-row-subtotal-value"><ReportComponents.Currency value={sumAvgCost} decimals={0} /></td>
                <td className="text-right pdf-data-table-summary-row-subtotal-value"><ReportComponents.Currency value={sumIdealBal} decimals={0} /></td>
                <td className="text-right pdf-data-table-summary-row-subtotal-value"><ReportComponents.Currency value={sumCurBal} decimals={0} /></td>
                <td className="text-right pdf-data-table-summary-row-subtotal-value"><ReportComponents.Currency value={sumMonthly} decimals={0} /></td>
            </tr>
        );

        return result;
    };

    const buildGrandTotalRow = (data: any) => {
        let sumAvgCost = 0;
        let sumIdealBal = 0;
        let sumCurBal = 0;
        let sumMonthly = 0;

        const groups = _.filter(data, ['group.first', true]);
        _.forEach(groups, (c) => {
            const groupSubtotals = _.get(c, 'group.subTotals');
            sumAvgCost += Number(_.get(groupSubtotals, 'avgCosts', 0));
            sumIdealBal += Number(_.get(groupSubtotals, 'idealBals', 0));
            sumCurBal += Number(_.get(groupSubtotals, 'currentFundBals', 0));
            sumMonthly += Number(_.get(groupSubtotals, 'monthly', 0));
        });

        const result = (
            <tr key={getTableRowKey('summary-row-grandtotal', sectionName, 'tbl-cfi-grand-total')} className="pdf-data-table-summary-row">
                <td colSpan={5} className="pdf-data-table-summary-row-colspan pdf-data-table-summary-row-grandtotal-title text-right">{appText('page.component-funding-information.grand-total')}:</td>
                <td className="text-right pdf-data-table-summary-row-grandtotal-value"><ReportComponents.Currency value={sumAvgCost} decimals={0} /></td>
                <td className="text-right pdf-data-table-summary-row-grandtotal-value"><ReportComponents.Currency value={sumIdealBal} decimals={0} /></td>
                <td className="text-right pdf-data-table-summary-row-grandtotal-value"><ReportComponents.Currency value={sumCurBal} decimals={0} /></td>
                <td className="text-right pdf-data-table-summary-row-grandtotal-value"><ReportComponents.Currency value={sumMonthly} decimals={0} /></td>
            </tr>
        );

        return result;
    };

    let subgroupsSorted = _.stubArray();
    if (reportOptionsGroupComponents) {
        subgroupsSorted = _.orderBy(subgroups, [g => g.subgroup, g => g.group.subgroupLabel, g => g.group.gItemInd]);
    } else {
        subgroupsSorted = FastSort(subgroups).by([
            { asc: '__componentSortBase' },
            { asc: '__componentSortDecimal' },
            { asc: 'componentName_sort' },
            { asc: '_id' },
            { asc: 'wrappedItemRow_sort' },
        ]);
    }
    subgroupsSorted = _.map(subgroupsSorted, (inv) => {
        if (inv.componentName_sort.substr(-10) === 'sort-num-0') {
            inv.wrappedItemParent = true;
        }
        return inv;
    });

    let tableRows = buildRowsNew(subgroupsSorted, 'buildRows-new');

    // Add grand total row
    const grandTotalRow = buildGrandTotalRow(subgroupsSorted);
    tableRows.push(grandTotalRow);

    const rowWouldWrap = (row: any) => {
        return (row.props.children.length === 9 &&
            ((_.size(row.props.children[1].props.children) > MAX_COMPONENT_NAME_LEN_WRAP) ||
                (row.props.children[4].props && (_.size(row.props.children[4].props.children) > MAX_QUANTITY_LEN_WRAP))));

    };

    const groupRowsToPages = (rows: any) => {
        let res: Array<any[]> = Array<any[]>();
        let rowCount = 0;
        let currentChunk: any = [];
        _.map(rows, (row, rowInd) => {
            if (rowWouldWrap(row)) {
                rowCount += 0.5;
            }
            if (rowCount < chunkSize) {
                currentChunk.push(row);
                rowCount++;
            } else {
                res[res.length] = [[...currentChunk]];
                currentChunk.length = 0;
                rowCount = 0;
                if (rowWouldWrap(row)) {
                    rowCount += 0.5;
                }
                currentChunk.push(row);
                rowCount++;
            }
            if (Number(rowInd) === _.size(rows) - 1) {
                res[res.length] = [[...currentChunk]];
            }
        });
        return res;
    };
    const pages = groupRowsToPages(tableRows);

    const pagesNew = _(pages)
        .map((chunkItems, chunkInd, chunkInputArr) => {
            const keyPrefix = `chunkInd-test-${chunkInd}`;

            const headRow = (
                <tr>
                    <th className="col-cfi-id">{appText('page.component-funding-information.grid-column-headers.id')}</th>
                    <th className="col-cfi-component-name">{appText('page.component-funding-information.grid-column-headers.component-name')}</th>
                    <th className="col-cfi-ul">{appText('page.component-funding-information.grid-column-headers.ul')}</th>
                    <th className="col-cfi-rul">{appText('page.component-funding-information.grid-column-headers.rul')}</th>
                    <th className="col-cfi-quantity">{appText('page.component-funding-information.grid-column-headers.quantity')}</th>
                    <th className="col-cfi-average-current-cost">{appText('page.component-funding-information.grid-column-headers.avg-current-cost')}</th>
                    <th className="col-cfi-ideal-balance">{appText('page.component-funding-information.grid-column-headers.ideal-balance')}</th>
                    <th className="col-cfi-current-fund-balance">{appText('page.component-funding-information.grid-column-headers.current-fund-balance')}</th>
                    <th className="col-cfi-monthly">{appText('page.component-funding-information.grid-column-headers.monthly')}</th>
                </tr>
            );

            let fundBalance = (<div />);
            const showFundBalance: boolean = ((Number(chunkInd) === _.size(chunkInputArr) - 1) || (_.size(chunkInputArr) === 0)) ? true : false;
            if (showFundBalance) {
                fundBalance = buildFundBalance(percentFunded);
            }

            const chunkPage = (
                <div key={getTableRowKey(`cfi-page-keyPrefix-${keyPrefix}`, sectionName, 'tbl-cgi-chunk-page')} className="pdf-data-table-component-funding-chunkpage">
                    <ReportPageWithRegister
                        className={className}
                        classNameTable="pdf-data-table-component-funding"
                        footerData={reportData}
                        pageIndex={chunkInd}
                        pageTitle={pageTitle}
                        showFooter={true}
                        tbodyRows={chunkItems}
                        theadRow={headRow}
                        totalPageCount={pageCount}
                    >
                        {fundBalance}
                    </ReportPageWithRegister>
                </div>
            );

            return chunkPage;
        })
        .value();

    return (
        <div className="pdf-data-table-component-funding-pages-wrapper">
            {pagesNew}
        </div>
    );
};



// WEBPACK FOOTER //
// ./src/report/pdf/pages/report-page-component-funding.tsx